import { useEffect } from 'react'
import { Alert, Form, Input, InputNumber, Modal } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import {
  CRITERION_DESCRIPTION_MAX,
  CRITERION_NAME_MAX,
  SORT_ORDER_MAX,
  createRatingCriterion,
  updateRatingCriterion,
  type RatingCriterion,
} from './ratingApi'

interface Props {
  open: boolean
  criterion: RatingCriterion | null
  onClose: () => void
}

interface FormValues {
  name: string
  description?: string
  sortOrder: number
}

export function RatingCriterionFormModal({ open, criterion, onClose }: Props) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()
  const isEdit = criterion !== null

  useEffect(() => {
    if (!open) return
    form.setFieldsValue(
      criterion
        ? {
            name: criterion.name,
            description: criterion.description ?? '',
            sortOrder: criterion.sortOrder,
          }
        : { name: '', description: '', sortOrder: 0 },
    )
  }, [open, criterion, form])

  const save = useMutation({
    mutationFn: (values: FormValues) => {
      const description = values.description?.trim() ?? ''
      const draft = {
        name: values.name.trim(),
        description: description === '' ? null : description,
        sortOrder: values.sortOrder,
      }
      return criterion ? updateRatingCriterion(criterion.id, draft) : createRatingCriterion(draft)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.hr.all })
      form.resetFields()
      onClose()
    },
  })

  const close = () => {
    save.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={isEdit ? t('hr.ratingCriterion.editTitle') : t('hr.ratingCriterion.createTitle')}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      confirmLoading={save.isPending}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      destroyOnHidden
      width={520}
    >
      {save.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(save.error, t)}
        />
      ) : null}

      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={save.isPending}>
        <Form.Item
          name="name"
          label={t('hr.ratingCriterion.name')}
          rules={[
            { required: true, whitespace: true, message: t('hr.ratingCriterion.validation.nameRequired') },
            { max: CRITERION_NAME_MAX, message: t('hr.person.validation.nameLength') },
          ]}
        >
          <Input autoFocus />
        </Form.Item>

        <Form.Item
          name="description"
          label={t('hr.ratingCriterion.description')}
          extra={t('hr.ratingCriterion.descriptionHint')}
          rules={[
            {
              max: CRITERION_DESCRIPTION_MAX,
              message: t('hr.ratingCriterion.validation.descriptionLength'),
            },
          ]}
        >
          <Input.TextArea rows={2} />
        </Form.Item>

        <Form.Item
          name="sortOrder"
          label={t('hr.ratingCriterion.sortOrder')}
          extra={t('hr.ratingCriterion.sortOrderHint')}
          rules={[{ required: true, message: t('hr.ratingCriterion.validation.sortOrderRequired') }]}
        >
          <InputNumber min={0} max={SORT_ORDER_MAX} precision={0} style={{ width: 160 }} />
        </Form.Item>

        {isEdit && criterion.ratingCount > 0 ? (
          <Alert
            type="info"
            showIcon
            message={t('hr.ratingCriterion.renameNote', { count: criterion.ratingCount })}
          />
        ) : null}
      </Form>
    </Modal>
  )
}
